import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { DetectMethodChanges } from '../../utils';
import { RollPositionsInputs } from './apg-copilot-domain';
import {
    CalendarDayState,
    calendarDayState,
    toggleSpecificDateExclusion,
    toggleWeekdayOverride
} from './apg-copilot-questionnaire';

// What Apply commits: the two draft sets the calendar edits, together. CR-20260904-001 added the
// weekday overrides (Mondays only until CR-20260915-002); they travel in the SAME event so one
// Apply is one atomic commit.
export interface ExclusionCalendarResult {
    specificDates: Date[];
    weekdayOverrides: Date[];
}

const MONTHS_FULL = ['January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'];

interface DayCell {
    date: Date | null;   // null = padding blank
    num: number | null;
}

interface MonthView {
    label: string;
    weeks: DayCell[][];
}

// Net-new custom exclusion calendar (no reusable component exists in the codebase). Renders the
// month(s) the chosen range spans; every day is classified by calendarDayState():
//   - out-of-range  -> unavailable (disabled)
//   - weekday-excluded (in range) -> crossed AND locked (complement invariant)
//   - other in-range -> shaded, click-toggleable
// It edits a DRAFT and stays open for multi-select; Apply commits the draft and closes.
@Component({
    selector: 'ets-apg-exclusion-calendar',
    templateUrl: './apg-exclusion-calendar.component.html',
    styleUrls: ['./apg-exclusion-calendar.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class ApgExclusionCalendarComponent implements OnInit {

    constructor(private readonly _changeDetector: ChangeDetectorRef) {}

    @Input() inputs: RollPositionsInputs;

    // Layer 2: the chain's listed expirations ("yyyy-MM-dd"). Only these in-range days are candidates;
    // every other in-range day (weekend, holiday, non-expiration weekday) renders unavailable.
    @Input() availableExpirations: string[] = [];

    @Output() apply = new EventEmitter<ExclusionCalendarResult>();
    @Output() cancel = new EventEmitter<void>();

    readonly dowHeaders = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

    // The working sets the user edits; seeded from the committed values and emitted on Apply.
    draft: Date[] = [];
    draftWeekdayOverrides: Date[] = [];
    months: MonthView[] = [];

    ngOnInit(): void {
        this.draft = (this.inputs && this.inputs.specificDateExclusions)
            ? this.inputs.specificDateExclusions.slice()
            : [];
        this.draftWeekdayOverrides = (this.inputs && this.inputs.weekdayOverrides)
            ? this.inputs.weekdayOverrides.slice()
            : [];
        this.buildMonths();
    }

    // Inputs with BOTH DRAFT sets substituted — so day states reflect the in-progress edit. The
    // override list must be here too, or a just-brought-back day would keep rendering struck
    // through until Apply.
    private workingInputs(): RollPositionsInputs {
        return {
            date: this.inputs.date,
            weekdayExclusions: this.inputs.weekdayExclusions,
            specificDateExclusions: this.draft,
            weekdayOverrides: this.draftWeekdayOverrides,
            bias: this.inputs.bias
        };
    }

    stateFor(cell: DayCell): CalendarDayState {
        if (!cell.date) {
            return 'unavailable';
        }
        return calendarDayState(this.workingInputs(), cell.date, this.availableExpirations);
    }

    dataElementFor(cell: DayCell): string {
        const st = this.stateFor(cell);
        if (st === 'in-range') {
            return 'calendar-day-in-range';
        }
        if (st === 'override-weekday') {
            return 'calendar-day-override-weekday';
        }
        if (st === 'excluded-weekday' || st === 'excluded-specific') {
            return 'calendar-day-excluded';
        }
        return 'calendar-day';
    }

    classFor(cell: DayCell): { [k: string]: boolean } {
        const st = this.stateFor(cell);
        return {
            'd-neutral': st === 'unavailable',
            'd-in': st === 'in-range',
            'd-ex': st === 'excluded-weekday' || st === 'excluded-specific',
            // A brought-back day: NOT struck through, and clickable (one click puts it back).
            'd-override': st === 'override-weekday',
            'd-locked': st === 'excluded-weekday' || st === 'unavailable'
        };
    }

    @DetectMethodChanges()
    onDayClick(cell: DayCell): void {
        if (!cell.date) {
            return;
        }
        const working = this.workingInputs();
        const st = this.stateFor(cell);
        // CR-20260904-001 BR-W3/BR-W4, generalised by CR-20260915-002 (owner ruling 1): a day the
        // WEEKDAY rule excluded (or that is already lifted) toggles the OVERRIDE — the escape
        // hatch, for any excluded weekday now, not Mondays only. Every other day keeps the
        // exclude-only path.
        if (st === 'excluded-weekday' || st === 'override-weekday') {
            const flipped = toggleWeekdayOverride(working, cell.date, this.availableExpirations);
            this.draftWeekdayOverrides = flipped.weekdayOverrides;
            return;
        }
        // toggleSpecificDateExclusion is a no-op for unavailable / weekday-excluded days, so the
        // complement invariant holds without any UI-side guard.
        const next = toggleSpecificDateExclusion(working, cell.date, this.availableExpirations);
        this.draft = next.specificDateExclusions;
    }

    onApply(): void {
        this.apply.emit({
            specificDates: this.draft.slice(),
            weekdayOverrides: this.draftWeekdayOverrides.slice()
        });
    }

    onCancel(): void {
        this.cancel.emit();
    }

    // Bulk-clear every MANUALLY excluded specific date (owner answer #3 -> empty the draft).
    // Weekday exclusions are a separate control and stay untouched (answer #2). Per-date
    // click-to-toggle still works (answer #1a). The calendar STAYS OPEN — Apply still commits
    // the (now empty) draft, Cancel still discards. OnPush repaint via @DetectMethodChanges.
    @DetectMethodChanges()
    onReset(): void {
        this.draft = [];
    }

    private isValid(d: Date | null | undefined): boolean {
        return d instanceof Date && !isNaN(d.getTime());
    }

    private buildMonths(): void {
        this.months = [];
        if (this.inputs.date.kind !== 'range') {
            return;
        }
        const s = this.inputs.date.start;
        const e = this.inputs.date.end;
        if (!this.isValid(s) || !this.isValid(e)) {
            return;
        }
        let sy = (s as Date).getFullYear();
        let sm = (s as Date).getMonth();
        let ey = (e as Date).getFullYear();
        let em = (e as Date).getMonth();
        // normalize order (start month <= end month)
        if (sy > ey || (sy === ey && sm > em)) {
            const ty = sy;
            const tm = sm;
            sy = ey;
            sm = em;
            ey = ty;
            em = tm;
        }
        const out: MonthView[] = [];
        let y = sy;
        let m = sm;
        let guard = 0;
        while ((y < ey || (y === ey && m <= em)) && guard < 24) {
            out.push(this.buildMonthGrid(y, m));
            m++;
            if (m > 11) { m = 0; y++; }
            guard++;
        }
        this.months = out;
    }

    private buildMonthGrid(year: number, month: number): MonthView {
        const first = new Date(year, month, 1);
        const startDay = first.getDay();                        // 0=Sun
        const daysInMonth = new Date(year, month + 1, 0).getDate();
        const cells: DayCell[] = [];
        for (let i = 0; i < startDay; i++) {
            cells.push({ date: null, num: null });
        }
        for (let d = 1; d <= daysInMonth; d++) {
            cells.push({ date: new Date(year, month, d), num: d });
        }
        while (cells.length % 7 !== 0) {
            cells.push({ date: null, num: null });
        }
        const weeks: DayCell[][] = [];
        for (let i = 0; i < cells.length; i += 7) {
            weeks.push(cells.slice(i, i + 7));
        }
        return { label: MONTHS_FULL[month] + ' ' + year, weeks };
    }
}
